<script setup lang='ts'>
import { withTrailingSlash } from 'ufo'
import { resolveCanonicalQuery } from '~/composables/resolveCanonicalQuery'
import { resolveCanonicalRedirect } from '~/composables/resolveCanonicalRedirect'
import { resolveSeoMetadata } from '~/composables/resolveSeoMetadata'
import useResolvedContent from '~/composables/useResolvedContent'
import { useSeo } from '~/composables/useSeo'

const { setupSchema } = useSchemaForContent()
const { resolvedContent, updateContentByUrl } = useResolvedContent()
const route = useRoute()
const siteConfig = useSiteConfig()
const requestEvent = useRequestEvent()

const { failed } = await updateContentByUrl(route.path)

// Backend unreachable / errored → render a graceful error page rather than letting
// the uncaught fetch error crash SSR (or falling through to render with no model).
// A genuine empty / NOTFOUND result renders the /404 page in place below.
if (failed) {
  throw createError({
    statusCode: 503,
    statusMessage: 'Content temporarily unavailable',
    fatal: true,
  })
}

const goneStatus = import.meta.server
  ? (requestEvent?.context.goneStatus as number | undefined)
  : undefined

const contentType = resolvedContent.value?.content?.contentType

const isPageContent = Array.isArray(contentType) && contentType.includes('Page') && !contentType.includes('NotFoundPage')

const isNotFound = goneStatus !== undefined
  || !resolvedContent.value
  || Object.keys(resolvedContent.value).length === 0
  || resolvedContent.value.status === 'NOTFOUND'
  || !isPageContent

// Real 404 for not-found (uncacheable — avoids the P180PCM-281 soft-404 poisoning); the
// _payload.js inherits it but server/plugins/payload-status.ts flips it to 200 for hydration.
if (isNotFound || route.path === '/404') {
  if (route.path !== '/404') {
    await updateContentByUrl('/404')
  }
  if (import.meta.server && requestEvent) {
    setResponseStatus(requestEvent, goneStatus ?? 404)
  }
}

if (!isNotFound && route.path !== '/404') {
  const modelUrl = (resolvedContent.value?.content as { url?: string } | undefined)?.url
  const canonicalRedirectTarget = resolveCanonicalRedirect(route.path, modelUrl)
  if (canonicalRedirectTarget) {
    await navigateTo(
      { path: canonicalRedirectTarget, query: route.query, hash: route.hash },
      { redirectCode: 301, replace: true },
    )
  }
}

// useRequestURL() is deliberately unused — it returns "http://localhost/..." under Workers SSR.
const canonicalUrl = computed(() => {
  const base = (siteConfig.url || '').replace(/\/$/, '')
  return `${base}${withTrailingSlash(route.path)}`
})

// nuxt-seo-utils would keep every whitelisted param, self-canonicalising
// `?page=1` and facet views onto URLs that duplicate the clean listing.
useHead(() => isNotFound
  ? {}
  : { link: [{ rel: 'canonical', href: `${canonicalUrl.value}${resolveCanonicalQuery(route.query)}` }] })

// Shared resolver used by both useSeo (HTML head / OG meta) and
// useSchemaForContent (JSON-LD) so all three SEO surfaces stay aligned.
const seo = computed(() =>
  resolvedContent.value.content
    ? resolveSeoMetadata(resolvedContent.value.content, canonicalUrl.value)
    : null,
)

watchEffect(() => {
  if (!seo.value) {
    return
  }

  useSeo(seo.value)
})

setupSchema(resolvedContent.value.content, canonicalUrl.value)
</script>

<template>
  <GenericsPage
    :model="resolvedContent?.content"
  />
</template>
